<template>
  <div class="strategies-table">
    <v-card-title class="toolbar primary white--text mb-0 pb-3">
      <div>
        <span class="headline">
          <h1 class="headline ml-2">
            <v-icon dark>description</v-icon>
            Saving Plans
          </h1>
        </span>
      </div>
      <v-spacer />
      <v-text-field
        v-model="filterString"
        append-icon="search"
        label="Filter results"
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
      />
    </v-card-title>
    <v-card-title class="toolbar primary white--textn pt-0">
      <v-select
        v-model="searchKey"
        :items="digitalDepotAccountSearchKeys"
        label="Search by ..."
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
        class="ml-2 mr-2"
      />
      <v-text-field
        v-model="searchValue"
        append-icon="search"
        label="search value"
        single-line
        flat
        dark
        color="primary"
        solo-inverted
        hide-details
        class="ml-2 mr-2"
      />
      <v-tooltip
        v-if="searchKey === 'userId' && searchValue"
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            class="right-margin"
            color="secondary"
            fab
            dark
            absolute
            bottom
            right
            @click.stop="onShowRecalculate(searchValue)"
            v-on="on"
          >
            <v-icon>restore</v-icon>
          </v-btn>
        </template>
        <span>Resend User Data</span>
      </v-tooltip>
      <v-tooltip
        v-if="searchValue"
        top
        dark
      >
        <template v-slot:activator="{ on }">
          <v-btn
            color="secondary"
            fab
            dark
            absolute
            bottom
            left
            @click.stop="onRefresh"
            v-on="on"
          >
            <v-icon>refresh</v-icon>
          </v-btn>
        </template>
        <span>Refresh</span>
      </v-tooltip>
    </v-card-title>

    <v-data-table
      :headers="headers"
      :items="filteredItems"
      :pagination.sync="pagination"
      :loading="isLoading"
      loading-text="Loading..."
      disable-initial-sort
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <tr @click="toggleSelected(props.item)">
          <td class="">
            {{ props.item.id }}
          </td>
          <td>
            {{ props.item.brand }}
          </td>
          <td>
            {{ props.item.userId }}
          </td>
          <td>
            {{ props.item.strategyName }}
          </td>
          <td>
            {{ props.item.type }}
          </td>
          <td>
            {{ props.item.autoInvestorConfiguration ?
              props.item.autoInvestorConfiguration.monthlyDeposit : '' }}
          </td>
          <td class="text-xs">
            {{ (new Date(props.item.timestamp)).toISOString() }}
          </td>
          <td
            v-if="!props.item.archived"
          >
            <v-icon
              v-if="props.item.active"
              color="green"
            >
              run_circle
            </v-icon>
            <v-icon
              v-else
              color="red"
            >
              stop_circle
            </v-icon>
          </td>
          <td
            v-else
          >
            <v-icon
              v-if="props.item.active"
              color="#dddddd"
            >
              run_circle
            </v-icon>
            <v-icon
              v-else
              color="#dddddd"
            >
              stop_circle
            </v-icon>
          </td>
          <td>
            <v-icon
              v-if="props.item.archived"
              color="red"
            >
              check_circle
            </v-icon>
            <v-icon
              v-else
              color="#dddddd"
            >
              cancel
            </v-icon>
          </td>
          <td class="text-xs">
            <tr>
              <v-tooltip
                top
                dark
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    dark
                    flat
                    icon
                    color="primary"
                    v-on="on"
                    @click.stop="() => onShowHistory(props.item)"
                  >
                    <v-icon>history</v-icon>
                  </v-btn>
                </template>
                <span>History</span>
              </v-tooltip>
            </tr>
            <tr>
              <v-tooltip
                top
                dark
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    dark
                    flat
                    icon
                    color="primary"
                    v-on="on"
                    @click.stop="() => onShowEdit(props.item)"
                  >
                    <v-icon>edit</v-icon>
                  </v-btn>
                </template>
                <span>Edit</span>
              </v-tooltip>
            </tr>
          </td>
        </tr>
        <tr v-if="props.item.id && selected === props.item.id">
          <td colspan="6">
            <ShowAccountSettings
              :strategy="props.item"
              :refresh="onRefresh"
            />
          </td>
        </tr>
      </template>
    </v-data-table>

    <ShowAccountHistory
      v-if="isHistory"
      :is-open="isHistory"
      :toggle-open="toggleHistory"
      :strategy-id="currentStrategy.id"
    />

    <ShowAccountEdit
      v-if="isEditing"
      :is-open="isEditing"
      :toggle-open="toggleEdit"
      :strategy-id="currentStrategy.id"
    />

    <RecalculateUserDialog
      v-if="isRecalculate"
      :is-open="isRecalculate"
      :toggle-open="toggleRecalculate"
      :user-id="recalculateUserId"
    />
  </div>
</template>

<script>
import _ from 'lodash';
import RecalculateUserDialog from './dialogs/account/RecalculateUserDialog';
import ShowAccountEdit from './dialogs/account/ShowAccountEdit';
import ShowAccountHistory from './dialogs/account/ShowAccountHistory';
import ShowAccountSettings from './dialogs/account/ShowAccountSettings';
import DigitalDepotAccountSearchKeys from '../../enum/digitalDepots/DigitalDepotAccountSearchKeys';

export default {
  name: 'RoboInvestingSavingPlansTable',
  components: {
    RecalculateUserDialog,
    ShowAccountEdit,
    ShowAccountHistory,
    ShowAccountSettings,
  },
  data() {
    return {
      currentStrategy: null,
      isLoading: false,
      isHistory: false,
      selected: 0,
      isEditing: false,
      isRecalculate: false,
      currEditStrategy: {},
      recalculateUserId: '',
      pagination: {
        rowsPerPage: '40',
        sortBy: 'Updated At',
        descending: true,
      },
      searchKey: null,
      searchValue: '',
      filterString: '',
      headers: [
        { text: 'ID', value: 'id' },
        { text: 'Brand', value: 'brand' },
        { text: 'User ID', value: 'user_id' },
        { text: 'Name', value: 'strategyName' },
        { text: 'Type', value: 'type' },
        { text: 'MonthlyDeposit', value: 'autoInvestorConfiguration.monthlyDeposit' },
        { text: 'Updated At', value: 'timestamp' },
        { text: 'Active', value: 'active' },
        { text: 'Archived', value: 'archived' },
        { text: 'Actions', value: null, sortable: false },
      ],
    };
  },
  computed: {
    strategies() {
      return this.$store.state.strategiesStore.strategies.filter(strategy => strategy.type === 'SAVINGS_PLAN');
    },
    digitalDepotAccountSearchKeys() {
      return Object.keys(DigitalDepotAccountSearchKeys).map(key => DigitalDepotAccountSearchKeys[key]);
    },
    filteredItems() {
      const { filterString } = this;

      return filterString.length < 1 ?
        this.strategies :
        this.strategies.filter((item) => {
          let found = false;
          Object.keys(item)
            .forEach((key) => {
              if (
                item[key] && String(item[key]).indexOf(filterString) > -1
              ) {
                found = true;
              }

              if (String(item.userId).indexOf(filterString) > -1) {
                found = true;
              }
            });

          return found;
        });
    },
  },
  watch: {
    searchKey() {
      this.searchValue = '';
    },
    searchValue(val) {
      if (val !== '') {
        this.reloadDigitalDepotAccounts(this.searchKey, val);
      }
    },
  },
  mounted() {
    this.reloadDigitalDepotAccounts = _.debounce(this.reloadDigitalDepotAccounts, 500);
  },
  methods: {
    async reloadDigitalDepotAccounts(key, value) {
      this.isLoading = true;
      await this.$store.dispatch('fetchDigitalDepotAccounts', { key, value });
      this.isLoading = false;
    },
    onRefresh() {
      this.reloadDigitalDepotAccounts(this.searchKey, this.searchValue);
    },
    toggleEdit() {
      this.isEditing = !this.isEditing;
    },
    toggleHistory() {
      this.isHistory = !this.isHistory;
    },
    toggleSelected(strategy) {
      this.selected = this.selected === strategy.id ? null : strategy.id;
    },
    toggleRecalculate() {
      this.isRecalculate = !this.isRecalculate;
    },
    onShowHistory(item) {
      this.isHistory = true;
      this.currentStrategy = item;
    },
    onShowEdit(item) {
      this.isEditing = true;
      this.currentStrategy = item;
    },
    onShowRecalculate(userId) {
      this.isRecalculate = true;
      this.recalculateUserId = userId;
    },
  },
};
</script>
<style lang="scss">
  .strategies-table {
    > .toolbar {
      position: relative;
      padding: 24px 24px 32px 24px;
      margin-bottom: 32px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/components/roboInvesting/RoboInvestingSavingPlansTable.vue